<!-- eslint-disable vue/require-explicit-emits -->
<template>
  <iRow
    :data-parent-id="identifier"
    :data-identifier="identifier"
    v-bind="rootProps"
    :class="classList"
    :draggable="editMode && isDraggable(identifier, configuration) && !breakpoint"
    @click.stop="() => elementConfig && internalHandleOnEdit()"
    @dragstart.stop="internalHandleDragStart"
    @dragend.stop="internalHandleDragEnd"
    @dragover.stop="internalHandleDragOver"
    @dragleave.stop="internalHandleDragLeave"
    @drop.stop="internalHandleDrop"
    @mouseover.stop="internalMouseOverHandler"
    @mouseleave.stop="internalMouseLeaveHandler"
    @contextmenu="openContextualMenuForBuilder"
  >
    <iIcon
      v-if="elementIsDeprecated"
      icon="exclamation-triangle"
      variant="error"
      size="small"
      class="deprecated-icon"
    />
    <Teleport to="body">
      <DynamicComponentContextualMenu
        v-if="canShowContextualMenu"
        :style="contextMenuFixedPositionStyle"
        :can-copy="!!elementConfig"
        :can-paste="!!elementConfig"
        :is-deletable="isDeletable(identifier, configuration)"
        :is-editable="isEditable(identifier, configuration)"
        :is-duplicatable="isDuplicatable(identifier, configuration)"
        :can-wrap-in-horizontal-container="canWrap(identifier, configuration)"
        :can-wrap-in-vertical-container="canWrap(identifier, configuration)"
        :can-create-custom-component="!isDynamicAppComponent && canCreateCustomComponent(identifier, configuration)"
        :is-templated-component="!isDynamicAppComponent && isTemplatedComponent"
        :name="name"
        :theme-id="0"
        @delete="internalHandleOnDelete"
        @edit="internalHandleOnEdit"
        @duplicate="internalHandleDuplicate"
        @close="setShowContextualMenuForIdInBuilder(null)"
        @wrap-in-horizontal-container="internalWrapInHorizontalContainer"
        @wrap-in-vertical-container="internalWrapInVerticalContainer"
        @wrap-selection-in-vertical-container="internalWrapSelectionInVerticalContainer"
        @wrap-selection-in-horizontal-container="internalWrapSelectionInHorizontalContainer"
        @create-custom-component="internalCreateCustomComponent"
        @copy="internalHandleCopy"
        @paste="internalHandlePaste"
        @create-new-template-from-here="internalCreateDynamicComponentFromIdentifier"
      />
    </Teleport>
    <div
      v-if="canResizeHorizontally && (mouseOver || selectedHoverBorder)"
      class="resize-handle left"
      @mousedown.stop="handleMouseDownResize($event, 'left')"
    />
    <div
      v-if="canResizeHorizontally && (mouseOver || selectedHoverBorder)"
      class="resize-handle right"
      @mousedown.stop="handleMouseDownResize($event, 'right')"
    />
    <div
      v-if="canResizeVertically && (mouseOver || selectedHoverBorder)"
      class="resize-handle top"
      @mousedown.stop="handleMouseDownResize($event, 'top')"
    />
    <div
      v-if="canResizeVertically && (mouseOver || selectedHoverBorder)"
      class="resize-handle bottom"
      @mousedown.stop="handleMouseDownResize($event, 'bottom')"
    />
    <iCarousel
      :height="myHeight"
      :width="myWidth"
      :style-overrides="{
        carouselScrollerPadding: 'none',
        carouselItemsPadding: myItemsPadding,
        paddingTop: myPadding.top,
        paddingRight: myPadding.right,
        paddingBottom: myPadding.bottom,
        paddingLeft: myPadding.left,
        marginTop: myMargins.top,
        marginRight: myMargins.right,
        marginBottom: myMargins.bottom,
        marginLeft: myMargins.left,
      }"
      :item-min-width="itemMinWidth"
      :item-max-width="itemMaxWidth"
      :gap="myGap"
      :arrows="showArrows"
      :snap="snap"
      :dots="showDots"
      :animations="carouselAnimations"
      :infinite="myInfiniteScroll"
      alignment="center"
      @mousedown="handleMouseEvent"
      @wheel="handleMouseEvent"
      @touchstart="handleMouseEvent"
      @touchmove="handleMouseEvent"
    >
      <slot />
      <iColumn
        v-if="editMode"
        :width="300"
        height="fill"
        :style-overrides="{
          borderRadius: 'large',
          backgroundColor: 'paper',
        }"
        vertical-align="middle"
        align="center"
      >
        <iColumn
          vertical-align="middle"
          width="hug"
          height="hug"
          align="center"
          :style-overrides="{
            marginTop: 'standard',
            marginLeft: 'standard',
            marginRight: 'standard',
            marginBottom: 'standard',
          }"
          @click.stop
          @mouseup.stop="addItemToCarousel"
        >
          <iButton
            variant="tertiary"
          >
            <iColumn vertical-align="middle" width="hug" align="center">
              <iIcon icon="plus" size="large" />
              <iText size="extraLarge">
                Add new item
              </iText>
            </iColumn>
          </iButton>
        </iColumn>
      </iColumn>
    </iCarousel>
  </iRow>
</template>

<script>
import DynamicComponentContextualMenu
  from "%/dynamic-components/render-engine/components/DynamicComponentContextualMenu.vue";
import CarouselMixin from "%/dynamic-components/render-engine/components/elements/layouts/carousel-mixin";
import CarouselOptions from "%/dynamic-components/render-engine/bundles/carousel-options";
import LayoutWidthHeightOptions from "%/dynamic-components/render-engine/bundles/layout-width-height-options";
import PositioningOptions from "%/dynamic-components/render-engine/bundles/positioning-options";
import AppearanceOptions from "%/dynamic-components/render-engine/bundles/appearance-options";
import SpacingOptions from "%/dynamic-components/render-engine/bundles/spacing-options";
import BorderOptions from "%/dynamic-components/render-engine/bundles/border-options";
import BackgroundOptions from "%/dynamic-components/render-engine/bundles/background-options";
import AnimationOptions from "%/dynamic-components/render-engine/bundles/animations-options";
import useDynamicComponentBuilderStore from "%/stores/dynamic-component-builder-store";
import AnimationEffectsOptions from "%/dynamic-components/render-engine/bundles/animation-effects-options";

export default {
  name: "CarouselContainer",
  components: { DynamicComponentContextualMenu },
  mixins: [CarouselMixin],
  optionBundles: [
    CarouselOptions,
    new LayoutWidthHeightOptions({
      "default.layout-height": "hug",
      "default.layout-width": "fill",
    }),
    PositioningOptions,
    new AppearanceOptions({}, {
      "border-radius": false,
    }),
    AnimationOptions,
    AnimationEffectsOptions,
    SpacingOptions,
    BorderOptions,
    new BackgroundOptions({}, {
      "background-image": true,
    }),
  ],
  computed: {
    canShowContextualMenu() {
      return (
        (this.isDynamicAppComponent ? true : this.canEditPublication) &&
        this.showContextualMenu &&
        this.contextMenuFixedPositionStyle.top &&
        this.contextMenuFixedPositionStyle.left
      );
    },
  },
  methods: {
    handleMouseEvent(event) {
      if (useDynamicComponentBuilderStore().currentlyInspectedElementIdentifiers.includes(this.identifier)) {
        event.stopPropagation();
      }
    },
  },
};
</script>
